import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Switch,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import {
  DELETE_LOAN_PROVIDER,
  TOGGLE_LOAN_PROVIDER_STATUS,
} from "apollo/mutuations";
import { LOAN_PROVIDERS } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import LoanProviderAddModal from "components/LoanProvider/LoanProviderAddModal";
import LoanProviderEditModal from "components/LoanProvider/LoanProviderEditModal";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { Trash } from "tabler-icons-react";

const LoanProviders = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);

  const { data, loading, refetch } = useQuery(LOAN_PROVIDERS, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      ordered_by: [
        {
          column: "CREATED_AT",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Loan Providers",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanProviders.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const [delLoanProvider] = useMutation(DELETE_LOAN_PROVIDER, {
    update(cache, { data: { deleteLoanProvider } }) {
      cache.updateQuery(
        {
          query: LOAN_PROVIDERS,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: searchValue,
            ordered_by: [
              {
                column: "CREATED_AT",
                order: "DESC",
              },
            ],
          },
        },
        (data) => {
          if (data.loanProviders.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              loanProviders: {
                data: [
                  ...data.loanProviders.data.filter(
                    (provider) => provider.id !== deleteLoanProvider.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const [toggleProviderStatus] = useMutation(TOGGLE_LOAN_PROVIDER_STATUS, {
    update(cache, { data: { toggleLoanProviderStatus } }) {
      // Update the cache to reflect the new status
      cache.modify({
        id: cache.identify({
          __typename: "LoanProvider",
          id: toggleLoanProviderStatus.id,
        }),
        fields: {
          status() {
            return toggleLoanProviderStatus.status;
          },
        },
      });
    },
    onCompleted(data) {
      showNotification({
        color: "green",
        title: "Success",
        message: "Provider Status Updated Successfully",
      });
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Update Status",
      });
      // Refetch on error to ensure UI is in sync
      refetch();
    },
  });

  const theme = useMantineTheme();

  const handleToggleStatus = (providerId, currentStatus) => {
    toggleProviderStatus({
      variables: { provider_id: providerId },
    });
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      width: "20%",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.name}</span>;
      },
    },
    {
      label: "Email",
      key: "email",
      sortable: false,
      searchable: true,
      width: "25%",
      render: (rowData) => {
        return <span>{rowData.contact_email || "N/A"}</span>;
      },
    },
    {
      label: "Code",
      key: "code",
      sortable: true,
      searchable: true,
      width: "10%",
      render: (rowData) => {
        return <span>{rowData.code}</span>;
      },
    },

    {
      label: "Phone",
      key: "contact_phone",
      sortable: false,
      searchable: true,
      width: "12%",
      render: (rowData) => {
        return <span>{rowData.contact_phone || "N/A"}</span>;
      },
    },
    {
      label: "Website",
      key: "website",
      sortable: false,
      searchable: true,
      width: "13%",
      render: (rowData) => {
        return rowData.website ? (
          <a
            href={rowData.website}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#FF6A00" }}
          >
            {rowData.website}
          </a>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      label: "Status",
      key: "status",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return (
          <Switch
            checked={rowData.status === 1}
            onChange={() => handleToggleStatus(rowData.id, rowData.status)}
            color="green"
            size="md"
          />
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "11%",
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton
              label="Edit"
              onClick={() => handleEditLoanProvider(rowData)}
            >
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: "Add Loan Provider",
    setAddModal: setOpened,
  };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const handleEditLoanProvider = (provider) => {
    setOpenedEdit(true);
    setEditId(provider);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const deleteLoanProvider = () => {
    delLoanProvider({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Loan Provider Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to Delete Loan Provider",
        });
      },
    });
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={opened}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpened(false)}
        position="left"
        size="40%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        <LoanProviderAddModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          size={size}
          refetch={refetch}
        />
      </Drawer>

      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="right"
        size="40%"
      >
        <LoanProviderEditModal
          setOpenedEdit={setOpenedEdit}
          editData={editId}
          data={data ? data.loanProviders.data : []}
        />
      </Drawer>

      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure you want to delete this loan provider?</p>
        <Group position="right">
          <Button onClick={() => deleteLoanProvider()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanProviders.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            optionsData={optionsData}
            loading={loading}
            data={data ? data.loanProviders.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanProviders;
